import { Storage } from 'aws-amplify'
import { type S3Object, type IStorageObject } from '../types/common'
import aws_config from '../../aws-exports'
import { uuid } from '.'

export enum StorageFolder {
  USER_PROFILE_PICTURE = 'user-profile',
  STUDIO_PROFILE_PICTURE = 'studio-profile-picture',
}

export interface SaveFilesToStorageInput {
  files: Array<{
    file: File
    filename?: string
  }>
  folder: StorageFolder
}

interface CreateFileKeyOptions {
  prefix?: string
  useFileNameInsteadOfUUID?: boolean
  removeExtension?: boolean
}

export default class StorageObject implements IStorageObject {
  readonly s3Object: S3Object

  constructor (s3Object: S3Object) {
    this.s3Object = s3Object
  }

  deleteFile = async (): Promise<void> => {
    await StorageObject.deleteFile(this.s3Object)
  }

  getPublicUrl = async (): Promise<string> => {
    const url = await Storage.get(this.s3Object.key, {
      download: false,
      region: this.s3Object.region,
      bucket: this.s3Object.bucket
    })
    return url
  }

  static deleteFile = async (s3Object: S3Object): Promise<void> => {
    await Storage.remove(s3Object.key)
  }

  static deleteFileFromFolder = async (storageFolder: StorageFolder, filename: string): Promise<void> => {
    const key = `${storageFolder}/${filename}`
    await Storage.remove(key)
  }

  static getFileExtension = (file: File): string => {
    const { type } = file
    return type.split('/')[1]
  }

  static createFileName = (file: File, options?: CreateFileKeyOptions): string => {
    const { type } = file
    let name = options?.useFileNameInsteadOfUUID ? file.name : uuid()
    if (options?.prefix) {
      name = `${options.prefix}-${name}`
    }
    if (options?.removeExtension) {
      return name
    }
    const extension = type.split('/')[1]
    return `${name}.${extension}`
  }

  static saveToStorage = async (
    file: File,
    storageFolder: StorageFolder,
    filename: string = uuid()
  ): Promise<StorageObject> => {
    const saveKey = `${storageFolder}/${filename}`
    const response = await Storage.put(saveKey, file, {
      contentType: file.type,
      level: 'public'
    })
    const { key: savedKey } = response
    const s3Object = {
      bucket: aws_config.aws_user_files_s3_bucket,
      region: aws_config.aws_user_files_s3_bucket_region,
      key: savedKey
    }
    return new StorageObject(s3Object)
  }

  static saveFilesToStorage = async (input: SaveFilesToStorageInput): Promise<StorageObject[]> => {
    const request = input.files.map(async (file) => {
      const filename = file.filename ?? uuid()
      const storageObject = await StorageObject.saveToStorage(file.file, input.folder, filename)
      return storageObject
    })
    const response = await Promise.all(request)
    return response
  }
}
